import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../services/apiClient';
import { saveImageToCloud, saveImageToTemporary } from '../../features/materials/api';
import { useToast } from '../../hooks/useToast';
import { useMaterialsPanelStore } from '../../stores/materialsPanelStore';
import { useAmazonAccounts } from '../../features/amazon/hooks';
import {
  getMarketplaceName,
  getAmazonDomain,
  getSellerCentralDomain,
  formatDimensions,
  formatPrice,
} from '../../features/amazon/marketplace';
import { Button } from '../../components/common/Button';
import { Tooltip } from '../../components/common/Tooltip';
import { BackButton } from '../../components/common/BackButton';
import { useProductImageSelection } from './shared/useProductImageSelection';
import { useProductMaterialFolders } from './shared/useProductMaterialFolders';
import { useProductImageDialogs } from './shared/useProductImageDialogs';
import { useConfirmDialog } from '../../components/common/useConfirmDialog';
import { ProductImageSlotsSection } from './shared/ProductImageSlotsSection';
import { ProductDetailsSection } from './shared/ProductDetailsSection';
import { AmazonBrandLinkPrompt } from './shared/AmazonBrandLinkPrompt';
import { toReorderItem, type ProductImageSlot } from './shared/productImageSlots';
import { AMAZON_SUB_SLOT_KEYS, amazonReorderDisabledReason, buildAmazonCatalogSlots, buildAmazonSlots, type AmazonImageSlot } from './amazonProductSlots';
import { buildAmazonReorderPayload, isSameOrder, type ReorderItem } from './shared/imageSlotReorder';
import { getErrorMessage } from '../../utils/getErrorMessage';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import './ProductsPageHeader.css';
import './ProductDetailPage.css';
import '../../styles/badges.css';

interface ProductData {
  id?: string; // amazon_products.id (UUID)
  asin: string;
  sku?: string;
  marketplace_id?: string;
  title?: string;
  description?: string;
  brand_name?: string;
  brand_master_name?: string;
  brand_id?: string;
  brand_amazon_brand_entity_id?: string | null;
  manufacturer?: string;
  size?: string;
  color?: string;
  model_number?: string;
  product_type?: string;
  category?: string[];
  barcode_value?: string;
  barcode_type?: string;
  price_amount?: number;
  price_currency?: string;
  bullet_points?: (string | { value: string })[];
  main_image_url?: string;
  additional_images?: Array<{
    url: string;
    width: number;
    height: number;
    variant: string;
  }>;
  /** スロット (main / pt01..pt08) ごとの画像の名前 (動画ストーリーボード用ラベル) */
  image_names?: Record<string, string>;
  item_dimensions?: {
    height?: { value: number; unit: string };
    width?: { value: number; unit: string };
    length?: { value: number; unit: string };
    weight?: { value: number; unit: string };
  };
  package_dimensions?: {
    height?: { value: number; unit: string };
    width?: { value: number; unit: string };
    length?: { value: number; unit: string };
    weight?: { value: number; unit: string };
  };
  last_fetched_at?: string;
  data_source?: string;
}

interface ImageSlotsResponse {
  asin: string;
  sku: string | null;
  source?: 'catalog';
  slots: Record<string, { url: string | null; filled: boolean; width?: number; height?: number; pending?: { url: string; uploaded_at: string } }>;
}

/** 反映待ちのスロットがあるあいだ、カタログ側の反映を拾うために画像枠を再取得する間隔 */
const REFLECTION_POLL_INTERVAL_MS = 60_000;

function toPendingReplacement(pending: { url: string; uploaded_at: string } | undefined): AmazonImageSlot['pending'] {
  return pending?.url ? { url: pending.url, uploadedAt: pending.uploaded_at } : undefined;
}

export default function ProductDetailPage() {
  const { platform, identifier } = useParams<{ platform: string; identifier: string }>();
  const navigate = useNavigate();

  // platformは現在amazonのみをサポート
  const asin = identifier;
  const queryClient = useQueryClient();
  const toast = useToast();
  const { contextData } = useMaterialsPanelStore();
  // 画像クリックで選択済み素材に追加/解除する共通フロー (組織スコープの素材一覧を使う)
  const { isImageSelected, handleAddToSelection } =
    useProductImageSelection({
      saveForSelection: (imageUrl) => saveImageToTemporary(imageUrl, asin),
    });
  const [bulletPointsExpanded, setBulletPointsExpanded] = useState(false);
  const [descriptionExpanded, setDescriptionExpanded] = useState(false);

  // 画像差替用の状態
  const [imageSlots, setImageSlots] = useState<AmazonImageSlot[]>([]);

  const [pendingDeletions, setPendingDeletions] = useState<Set<string>>(new Set());
  const [pendingReplacements, setPendingReplacements] = useState<Map<string, string>>(new Map());
  // 画像枠の並べ替えモード: 編集中の並び (null = 通常表示)
  const [reorderItems, setReorderItems] = useState<ReorderItem[] | null>(null);

  const [isRefreshing, setIsRefreshing] = useState(false);

  // 商品情報をAmazonから再取得
  const refreshProductMutation = useMutation({
    mutationFn: async () => {
      if (!amazonAccounts[0]?.id) {
        throw new Error('Amazonアカウントが見つかりません');
      }
      const response = await apiClient.post('/amazon/products/bulk', {
        asins: [asin],
        account_id: amazonAccounts[0].id,
      });
      return response.data;
    },
    onSuccess: (data) => {
      setIsRefreshing(false);
      if (data.success_count > 0) {
        toast.success('商品情報を更新しました');
        queryClient.invalidateQueries({ queryKey: ['product-detail', asin] });
        refetchImageSlots();
        setPendingDeletions(new Set());
      } else {
        toast.error('商品情報の更新に失敗しました');
      }
    },
    onError: (error: Error) => {
      setIsRefreshing(false);
      toast.error(`更新に失敗しました: ${error.message}`);
    },
  });

  const handleRefreshProduct = () => {
    setIsRefreshing(true);
    refreshProductMutation.mutate();
  };

  // platformは現在amazonのみをサポート (未対応ならデータ取得せず、描画側でメッセージを出す)。
  // hook の呼び出し順を固定するため早期 return はせず、JSX 側で分岐する
  const platformSupported = !platform || platform === 'amazon';

  const { data, isLoading, error } = useQuery({
    queryKey: ['product-detail', asin],
    queryFn: async () => {
      // まず、データベースから取得を試みる
      const response = await apiClient.get(`/amazon/products/${asin}`);
      return response.data.product;
    },
    enabled: !!asin && platformSupported,
  });

  // 取得前・名前が空なら undefined → routes.tsx の handle が使われる
  useDocumentTitle(data?.title || undefined);

  // Amazon Seller Accountsを取得
  const { accounts: amazonAccounts, isLoading: isLoadingAmazonAccounts } = useAmazonAccounts();

  // 画像スロット情報を取得
  const { data: imageSlotsData, isLoading: isLoadingImageSlots, isError: isErrorImageSlots, refetch: refetchImageSlots } = useQuery({
    queryKey: ['amazon-product-images', asin, amazonAccounts[0]?.id],
    queryFn: async (): Promise<ImageSlotsResponse | null> => {
      if (!amazonAccounts[0]?.id) return null;
      const response = await apiClient.get<ImageSlotsResponse>(`/amazon/products/${asin}/images`, {
        params: { account_id: amazonAccounts[0].id }
      });
      return response.data;
    },
    enabled: !!asin && platformSupported && amazonAccounts.length > 0,
    // 反映待ち (差し替え送信済み・Amazon 未反映) のスロットがあるあいだは定期的に取り直し、反映されたら自然に切り替える
    refetchInterval: (query) =>
      Object.values(query.state.data?.slots ?? {}).some((slot) => slot?.pending) ? REFLECTION_POLL_INTERVAL_MS : false,
  });

  // カタログ画像のみ（SKUなし＝自社管理でない商品）の場合、差替・削除を無効化
  const isCatalogOnly = imageSlotsData?.source === 'catalog';

  // 画像スロット情報を初期化
  useEffect(() => {
    if (imageSlotsData?.slots) {
      const slots: AmazonImageSlot[] = [
        {
          slot: 'main',
          url: imageSlotsData.slots.main?.url || null,
          filled: imageSlotsData.slots.main?.filled || false,
          label: 'メイン画像',
          pending: toPendingReplacement(imageSlotsData.slots.main?.pending),
        },
      ];

      for (let i = 1; i <= 8; i++) {
        const slotKey = `pt${i.toString().padStart(2, '0')}`;
        slots.push({
          slot: slotKey,
          url: imageSlotsData.slots[slotKey]?.url || null,
          filled: imageSlotsData.slots[slotKey]?.filled || false,
          label: `画像 ${i}`,
          pending: toPendingReplacement(imageSlotsData.slots[slotKey]?.pending),
        });
      }

      setImageSlots(slots);
    }
  }, [imageSlotsData]);

  const productUuid: string | undefined = data?.id;

  const saveImageToCloudMutation = useMutation({
    mutationFn: ({ url, asin }: { url: string; asin?: string }) => saveImageToCloud(url, asin),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cloud-materials'] });
      queryClient.invalidateQueries({ queryKey: ['cloud-materials-all'] });
      queryClient.invalidateQueries({ queryKey: ['product-linked-materials', 'cloud', productUuid] });
      toast.success('画像を共有フォルダに保存しました');
    },
    onError: (error: Error) => {
      toast.error(`保存に失敗しました: ${error.message}`);
    },
  });

  const saveImageToTemporaryMutation = useMutation({
    mutationFn: ({ url, asin }: { url: string; asin?: string }) => saveImageToTemporary(url, asin),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['temporary-materials'] });
      queryClient.invalidateQueries({ queryKey: ['temporary-materials-all'] });
      queryClient.invalidateQueries({ queryKey: ['product-linked-materials', 'temporary', productUuid] });
      toast.success('画像を個人フォルダに保存しました');
    },
    onError: (error: Error) => {
      toast.error(`保存に失敗しました: ${error.message}`);
    },
  });

  const deleteProductMutation = useMutation({
    mutationFn: async (asin: string) => {
      const response = await apiClient.delete(`/amazon/products/${asin}`);
      return response.data;
    },
    onSuccess: () => {
      toast.success('商品を削除しました');
      navigate('/products/amazon');
    },
    onError: (error: Error) => {
      toast.error(`削除に失敗しました: ${error.message}`);
    },
  });

  // 画像アップロードのmutation
  const uploadImageMutation = useMutation({
    mutationFn: async ({ slot, imageUrl }: { slot: string; imageUrl: string }) => {
      if (!amazonAccounts[0]?.id) {
        throw new Error('Amazonアカウントが見つかりません');
      }
      const response = await apiClient.post(`/amazon/products/${asin}/images`, {
        account_id: amazonAccounts[0].id,
        images: { [slot]: imageUrl },
      });
      return { ...response.data, _isDelete: !imageUrl, _slot: slot };
    },
    onSuccess: (data) => {
      const marketplaceName = platform === 'amazon' ? 'Amazon' : platform || '';
      if (data._isDelete) {
        toast.success(`${marketplaceName}への画像削除リクエストを送信しました。反映まで数分かかる場合があります。`);
        // pendingDeletionsは維持（グレーアウト表示を継続）
      } else {
        toast.success('画像を送信しました。Amazonへの反映まで数分かかります。それまではこの画面に送信した画像を表示します', 8000);
        // 差替処理完了 → pendingReplacementsから削除 (以降は BE の slots[].pending が反映待ちとして表示を引き継ぐ)
        setPendingReplacements(prev => {
          const next = new Map(prev);
          next.delete(data._slot);
          return next;
        });
      }
      queryClient.invalidateQueries({ queryKey: ['product-detail', asin] });
      refetchImageSlots();
    },
    onError: (error: any, variables) => {
      const errorMessage = error.response?.data?.error || error.message || '画像のアップロードに失敗しました';
      toast.error(`アップロードエラー: ${errorMessage}`);
      // 失敗時はpending状態を解除
      if (!variables.imageUrl) {
        setPendingDeletions(prev => {
          const next = new Set(prev);
          next.delete(variables.slot);
          return next;
        });
      } else {
        setPendingReplacements(prev => {
          const next = new Map(prev);
          next.delete(variables.slot);
          return next;
        });
      }
    },
  });

  // 画像枠の並べ替え: 「完了」で変わった枠をまとめて 1 回の POST で送る (差替と同じエンドポイント・1コール分のポイント)。
  // moved_from で「先の枠に置いた画像はどの枠にあったか」を伝え、画像の名前を画像と一緒に動かす。
  // 送信後は差替と同じ反映待ち機構 (BE の slots[].pending) に乗る
  const reorderImagesMutation = useMutation({
    mutationFn: async ({ images, movedFrom }: { images: Record<string, string>; movedFrom: Record<string, string> }) => {
      if (!amazonAccounts[0]?.id) {
        throw new Error('Amazonアカウントが見つかりません');
      }
      const response = await apiClient.post(`/amazon/products/${asin}/images`, {
        account_id: amazonAccounts[0].id,
        images,
        moved_from: movedFrom,
      });
      return response.data;
    },
    onSuccess: (_data, variables) => {
      toast.success('画像の並び順を送信しました。Amazonへの反映まで数分かかります。それまではこの画面に送信した並びを表示します', 8000);
      setReorderItems(null);
      // 送信完了 → 以降は BE の slots[].pending が反映待ちとして表示を引き継ぐ
      setPendingReplacements(prev => {
        const next = new Map(prev);
        Object.keys(variables.images).forEach((key) => next.delete(key));
        return next;
      });
      queryClient.invalidateQueries({ queryKey: ['product-detail', asin] });
      refetchImageSlots();
    },
    onError: (error: unknown, variables) => {
      toast.error(`並べ替えエラー: ${getErrorMessage(error, '並び順の送信に失敗しました')}`);
      setPendingReplacements(prev => {
        const next = new Map(prev);
        Object.keys(variables.images).forEach((key) => next.delete(key));
        return next;
      });
      setPendingDeletions(prev => {
        const next = new Set(prev);
        Object.keys(variables.images).forEach((key) => next.delete(key));
        return next;
      });
    },
  });

  // 画像の名前 (image_names) を更新。自社DBのみ更新し、Amazon (SP-API) には何も送らない
  const updateImageNameMutation = useMutation({
    mutationFn: async ({ slot, name }: { slot: string; name: string }) => {
      const response = await apiClient.patch(`/amazon/products/${asin}/image-names`, { slot, name });
      return response.data;
    },
    onSuccess: (_data, variables) => {
      toast.success(variables.name ? '画像の名前を更新しました' : '画像の名前をクリアしました');
      queryClient.invalidateQueries({ queryKey: ['product-detail', asin] });
    },
    onError: (error: Error) => {
      toast.error(`名前の更新に失敗しました: ${error.message}`);
    },
  });

  const productData: ProductData | null = data || null;

  // スロット (main / pt01..pt08) → 画像の名前
  const imageNames: Record<string, string> = productData?.image_names || {};

  // ---- 共通の画像枠セクションに渡す枠モデル (アダプタ) ----
  // GET のスロット + 画面内の処理中状態 + 名前 から組み立てる。反映待ち (サーバー) と処理中 (画面内) は分けて持つ
  const productSlots: ProductImageSlot[] = buildAmazonSlots({
    imageSlots,
    pendingReplacements,
    pendingDeletions,
    imageNames,
    mainResolution: imageSlotsData?.slots?.main?.width && imageSlotsData?.slots?.main?.height
      ? { width: imageSlotsData.slots.main.width, height: imageSlotsData.slots.main.height }
      : null,
  });
  // Amazon アカウント未接続のとき: DB のカタログ画像から同じ枠モデルを組む (閲覧専用)
  const catalogSlots: ProductImageSlot[] = buildAmazonCatalogSlots({
    mainImageUrl: productData?.main_image_url,
    additionalImages: productData?.additional_images,
    imageNames,
  });


  const handleSaveToTemporary = (imageUrl: string) => {
    saveImageToTemporaryMutation.mutate({ url: imageUrl, asin });
  };

  const handleSaveToCloud = (imageUrl: string) => {
    saveImageToCloudMutation.mutate({ url: imageUrl, asin });
  };

  // 一括アップロード対象 = 表示中の商品画像
  // (Seller アカウント連携時はスロット画像、未連携時は DB に保存済みの画像)
  const allImageUrls: string[] = [];
  if (imageSlots.length > 0) {
    for (const slot of imageSlots) {
      if (slot.url) allImageUrls.push(slot.url);
    }
  } else if (productData) {
    if (productData.main_image_url?.trim()) allImageUrls.push(productData.main_image_url);
    for (const img of productData.additional_images ?? []) {
      if (img.url?.trim()) allImageUrls.push(img.url);
    }
  }

  // ヘッダーのフォルダ別カード (件数・アップロード済み判定・一括アップロード) は共通フック
  const folders = useProductMaterialFolders({
    productId: productUuid,
    productType: 'amazon',
    imageUrls: allImageUrls,
    saveToCloud: (url) => saveImageToCloud(url, asin),
    saveToTemporary: (url) => saveImageToTemporary(url, asin),
    card: {
      helpText: (folder) => folder === 'shared'
        ? 'この商品のAmazon商品画像を共有フォルダに「素材」としてアップロードすると、組織のメンバー全員が制作スタジオなどで利用できるようになります。'
        : 'この商品のAmazon商品画像を個人フォルダに「素材」としてアップロードすると、あなただけが制作スタジオなどで利用できるようになります。',
      countLabel: 'この商品の素材数',
      imageNoun: '商品画像',
    },
  });
  const { isImageSaved, isImageSavedInTemporary, materialLinksEnabled, renderFolderCard } = folders;

  // 削除などの確認はブラウザの confirm でなく独自モーダルで出す (出ない環境がある・利用者指示 2026-10-30)
  const { confirm: confirmAction, dialog: confirmDialog } = useConfirmDialog();

  const handleDelete = async () => {
    if (!(await confirmAction({ title: 'この商品を削除してもよろしいですか？', confirmLabel: '削除する' }))) return;
    deleteProductMutation.mutate(asin!);
  };

  // 画像差替ボタンをクリックしたときの処理
  const handleReplaceImage = (slotKey: string) => {
    openMaterialSelector(slotKey);
  };

  // 素材選択パネルを開く (選んだら差替確認ダイアログへ)
  const openMaterialSelector = (slotKey: string) => {
    const slot = productSlots.find((item) => item.key === slotKey);
    if (!slot) return;
    useMaterialsPanelStore.getState().open('select', {
      targetAsin: asin,
      targetBrandId: productData?.brand_id,
      targetSlot: slotKey,
      onSelect: (imageUrl: string) => dialogs.openReplaceConfirm(slot, imageUrl),
    });
  };

  // 差替確認・名前編集ダイアログ (共通フック)。
  // 差替は送信を開始して即 resolve → ダイアログは即閉じる (結果はトースト)。
  // 名前は mutateAsync の結果で閉じる/閉じないが決まる (失敗時は入力を残す)
  const dialogs = useProductImageDialogs({
    replaceTitle: (slot) => `Amazonの${slot.label}を差し替えますか？`,
    onConfirmReplace: async (slot, newUrl) => {
      setPendingReplacements(prev => new Map(prev).set(slot.key, newUrl));
      uploadImageMutation.mutate({ slot: slot.key, imageUrl: newUrl });
    },
    onSaveName: async (slot, name) => {
      await updateImageNameMutation.mutateAsync({ slot: slot.key, name });
    },
  });

  // 画像を削除（空文字列をアップロード）
  const handleDeleteImage = async (slotKey: string) => {
    const slot = imageSlots.find(s => s.slot === slotKey);
    if (!slot?.url) return;
    if (pendingDeletions.has(slotKey)) return; // 既に削除処理中
    if (!(await confirmAction({ title: `${slot.label}を削除しますか？`, message: 'Amazonの出品画像からも削除されます。', confirmLabel: '削除する' }))) return;
    setPendingDeletions(prev => new Set(prev).add(slotKey));
    uploadImageMutation.mutate({ slot: slotKey, imageUrl: '' });
  };

  // ---- 画像枠の並べ替えモード ----
  // 「並べ替え」で画像 1〜8 のカードをつかんで動かせるようにし、「完了」で変わった枠をまとめて送る。
  // メイン画像は Amazon では特別扱いのため並べ替えの対象外 (固定表示)。
  // 配列契約: items は pt01..pt08 の8要素 (main を含まない)。key は要素と一緒に移動する
  // Seller アカウントの接続有無で、画像の取得元と操作の可否が変わる (同じページ・同じコンポーネントで描画する):
  //   接続あり: GET /images (SP-API) のスロット。SKU がある出品商品なら差替・削除・並べ替えができる
  //   接続あり・SKU なし (カタログ参照のみ・source='catalog'): 画像は出せるが Amazon 側を変えられない
  //   接続なし: DB のカタログ画像 (catalogSlots)。閲覧専用
  // 編集できないときも「並べ替え」「再取得」は非活性で出し、理由をツールチップで示す
  const isConnected = amazonAccounts.length > 0;
  const canEditImages = isConnected && !isCatalogOnly;
  const slotsForView: ProductImageSlot[] = isConnected ? productSlots : catalogSlots;
  const subProductSlots = productSlots.slice(1);
  const currentReorderItems = (): ReorderItem[] => subProductSlots.map(toReorderItem);
  const reorderDisabledReason: string | null = !isConnected
    ? 'Amazonアカウントが接続されていないため並べ替えできません'
    : isCatalogOnly
      ? 'Amazonに出品していない (SKUが無い) 商品の画像は並べ替えできません'
      : amazonReorderDisabledReason({ subSlots: subProductSlots, isUploading: uploadImageMutation.isPending });
  const canStartReorder = canEditImages && reorderDisabledReason === null;
  const reorderDirty = !!reorderItems && !isSameOrder(currentReorderItems(), reorderItems);

  const startReorder = () => {
    if (!canStartReorder) return;
    setReorderItems(currentReorderItems());
  };

  const cancelReorder = () => {
    if (reorderImagesMutation.isPending) return;
    setReorderItems(null);
  };

  const completeReorder = () => {
    if (!reorderItems || reorderImagesMutation.isPending) return;
    const { images, movedFrom, emptied } = buildAmazonReorderPayload(AMAZON_SUB_SLOT_KEYS, currentReorderItems(), reorderItems);
    if (Object.keys(images).length === 0) {
      setReorderItems(null);
      return;
    }
    // 送信中は差替と同じ「処理中」表示にし、空になった枠は削除と同じ扱いにする
    setPendingReplacements(prev => {
      const next = new Map(prev);
      Object.entries(images).forEach(([key, url]) => { if (url) next.set(key, url); });
      return next;
    });
    if (emptied.length > 0) setPendingDeletions(prev => new Set([...prev, ...emptied]));
    reorderImagesMutation.mutate({ images, movedFrom });
  };


  if (!platformSupported) {
    return (
      <div className="product-detail-page">
        <div className="error-message">
          現在はAmazon商品のみをサポートしています
        </div>
      </div>
    );
  }

  if (!platform || !identifier) {
    return (
      <div className="product-detail-page">
        <div className="error-message">プラットフォームまたは商品IDが指定されていません</div>
      </div>
    );
  }

  return (
    <div className="product-detail-page">
      <div className="header-actions">
        <BackButton />
      </div>

      <header className="page-header page-header--child">
        <div className="page-header-left">
          <h1 className="page-title">{!isLoading && productData ? productData.title || '商品詳細' : '商品詳細'}</h1>
          {!isLoading && productData && (
            <p className="page-description">
              ASIN: {productData.asin}
              {productData.sku && ` | SKU: ${productData.sku}`}
              {(productData.brand_master_name || productData.brand_name) && (
                <>
                  {' | ブランド: '}
                  {productData.brand_id ? (
                    <Link to={`/brands/${productData.brand_id}`} className="brand-link">
                      {productData.brand_master_name || productData.brand_name}
                    </Link>
                  ) : (
                    productData.brand_name
                  )}
                  {productData.brand_id && !productData.brand_amazon_brand_entity_id && (
                    <>
                      {' '}
                      <BrandRegistryUnregisteredBadge />
                    </>
                  )}
                </>
              )}
            </p>
          )}
        </div>
        {materialLinksEnabled && (
          <div className="page-header-actions material-folder-actions">
            {renderFolderCard('shared')}
            {renderFolderCard('personal')}
          </div>
        )}
      </header>

      {isLoading || isLoadingAmazonAccounts ? (
        <div className="loading-state">
          <div className="spinner"></div>
          <p>読み込み中...</p>
        </div>
      ) : error ? (
        <div className="error-message">
          商品情報の取得に失敗しました
        </div>
      ) : !productData ? (
        <div className="error-message">
          商品データが見つかりません
        </div>
      ) : (
        <section className="product-result">
          {/* 画像スロット一覧: 接続ありは GET /images の読み込み・失敗を先に出し、揃ったら共通セクション。接続なしは DB の画像で即描画 */}
          {isConnected && isLoadingImageSlots && (
            <div className="image-slots-loading">
              <div className="loading-content">
                <div className="loading-spinner"></div>
                <p>商品画像を読み込んでいます...</p>
              </div>
            </div>
          )}
          {isConnected && !isLoadingImageSlots && (isErrorImageSlots || imageSlots.length === 0) && (
            <div className="image-slots-empty">
              <p>画像を取得できませんでした</p>
              <button type="button" className="retry-button" onClick={() => refetchImageSlots()}>
                再取得
              </button>
            </div>
          )}
          {(!isConnected || (!isLoadingImageSlots && !isErrorImageSlots && imageSlots.length > 0)) && (
            <ProductImageSlotsSection
              slots={slotsForView}
              capabilities={{
                select: true,
                save: true,
                replace: canEditImages,
                delete: canEditImages,
                editName: true,
                fileDrop: false,
                emptyClick: canEditImages,
                // 編集できないときもボタンは出し、理由を reorder.disabledReason のツールチップで示す
                reorder: true,
              }}
              deleteButton="actions"
              highlightedSlotKey={contextData?.targetSlot ?? null}
              emptyText={canEditImages ? '画像を選択' : '画像なし'}
              actionTooltips={(slot) => ({ replace: `Amazonの${slot.label}を差替`, delete: `Amazonから${slot.label}を削除` })}
              isImageSelected={isImageSelected}
              onSelectImage={(slot) => slot.url && handleAddToSelection(slot.url)}
              onReplace={(slot) => handleReplaceImage(slot.key)}
              onDelete={(slot) => void handleDeleteImage(slot.key)}
              onEditName={dialogs.openNameEdit}
              onEmptyClick={(slot) => handleReplaceImage(slot.key)}
              onDropFile={() => undefined}
              saveButtons={{
                isSavedCloud: isImageSaved,
                isSavedTemp: isImageSavedInTemporary,
                pendingCloud: saveImageToCloudMutation.isPending,
                pendingTemp: saveImageToTemporaryMutation.isPending,
                onSaveCloud: handleSaveToCloud,
                onSaveTemp: handleSaveToTemporary,
              }}
              replaceDisabled={uploadImageMutation.isPending}
              reorder={{
                disabledReason: reorderDisabledReason,
                items: reorderItems,
                labelAt: (index) => `画像 ${index + 1}`,
                isDirty: reorderDirty,
                isSubmitting: reorderImagesMutation.isPending,
                submitLabel: '送信中...',
                hint: '画像カードのドラッグで並び替え',
                onStart: startReorder,
                onCancel: cancelReorder,
                onChange: setReorderItems,
                onComplete: completeReorder,
              }}
            />
          )}

          {/* 商品情報セクション (共通の枠組み): 見出し行の右に再取得 (接続なしは非活性 + 理由のツールチップ)、下部に削除 */}
          <ProductDetailsSection
            title="Amazon商品情報"
            headerActions={
              <Tooltip
                content="Amazonアカウントが接続されていないため再取得できません"
                disabled={isConnected}
                className="refresh-button-tooltip"
              >
                <Button
                  type="button"
                  variant="muted"
                  size="sm"
                  onClick={isConnected ? handleRefreshProduct : undefined}
                  disabled={!isConnected || isRefreshing}
                  aria-disabled={!isConnected || undefined}
                  className="refresh-product-button"
                >
                  {isRefreshing ? (
                    <>
                      <img src="/refresh.svg" alt="" className="refresh-icon spinning" />
                      取得中...
                    </>
                  ) : (
                    <>
                      <img src="/refresh.svg" alt="" className="refresh-icon" />
                      商品情報をAmazonから再取得
                    </>
                  )}
                </Button>
              </Tooltip>
            }
            footer={
              <Button
                variant="danger"
                size="md"
                onClick={() => void handleDelete()}
                disabled={deleteProductMutation.isPending}
              >
                <img src="/bin_1_white.svg" alt="" className="delete-button-icon" />
                {deleteProductMutation.isPending ? '削除中...' : '商品一覧から削除'}
              </Button>
            }
          >
            <ProductDetailsTable
              productData={productData}
              variant={isConnected ? 'full' : 'basic'}
              bulletPointsExpanded={bulletPointsExpanded}
              setBulletPointsExpanded={setBulletPointsExpanded}
              descriptionExpanded={descriptionExpanded}
              setDescriptionExpanded={setDescriptionExpanded}
            />
          </ProductDetailsSection>
        </section>
      )}

      {/* 画像差替確認・名前編集モーダル (共通フック) */}
      {dialogs.dialogs}
      {confirmDialog}
    </div>
  );
}

/**
 * Amazon商品の詳細テーブル。
 *
 * variant='full' (Seller アカウント連携あり): Seller Central リンク・
 * ブランドマスタ名・情報ソース行を表示。
 * variant='basic' (連携なし): 上記を省いた基本情報のみ。
 */
function ProductDetailsTable({
  productData,
  variant,
  bulletPointsExpanded,
  setBulletPointsExpanded,
  descriptionExpanded,
  setDescriptionExpanded,
}: {
  productData: ProductData;
  variant: 'full' | 'basic';
  bulletPointsExpanded: boolean;
  setBulletPointsExpanded: (v: boolean) => void;
  descriptionExpanded: boolean;
  setDescriptionExpanded: (v: boolean) => void;
}) {
  const isFull = variant === 'full';
  // full はブランドマスタ名 (brand_master_name) を優先、basic は Amazon の brand_name のみ
  const brandDisplayName = isFull
    ? productData.brand_master_name || productData.brand_name
    : productData.brand_name;

  return (
    <table className="details-table">
      <tbody>
        {/* 商品名 */}
        {productData.title && (
          <tr>
            <th>商品名</th>
            <td colSpan={3}>{productData.title}</td>
          </tr>
        )}

        {/* この商品について */}
        {productData.bullet_points && productData.bullet_points.length > 0 && (
          <tr>
            <th>この商品について</th>
            <td colSpan={3} className="bullet-points-cell">
              <div className={`bullet-points ${bulletPointsExpanded ? 'expanded' : 'collapsed'}`}>
                <ul>
                  {(bulletPointsExpanded
                    ? productData.bullet_points
                    : productData.bullet_points.slice(0, 1)
                  ).map((point, index) => {
                    const text = typeof point === 'string' ? point : point?.value || '';
                    return <li key={index}>{text}</li>;
                  })}
                </ul>
                {productData.bullet_points.length > 1 && (
                  <div
                    className="expand-toggle"
                    onClick={() => setBulletPointsExpanded(!bulletPointsExpanded)}
                  >
                    <span className={`toggle-icon ${bulletPointsExpanded ? 'expanded' : ''}`}>
                      ›
                    </span>
                    <span className="toggle-text">
                      {bulletPointsExpanded ? '折りたたむ' : 'もっと見る'}
                    </span>
                  </div>
                )}
              </div>
            </td>
          </tr>
        )}

        {/* 説明 */}
        {productData.description && (
          <tr>
            <th>商品の説明</th>
            <td colSpan={3} className="description-cell">
              <div className={`description-content ${descriptionExpanded ? 'expanded' : 'collapsed'}`}>
                <div className="description-text">
                  {descriptionExpanded
                    ? productData.description
                    : productData.description.length > 150
                    ? `${productData.description.substring(0, 150)}...`
                    : productData.description}
                </div>
                {productData.description.length > 150 && (
                  <div
                    className="expand-toggle"
                    onClick={() => setDescriptionExpanded(!descriptionExpanded)}
                  >
                    <span className={`toggle-icon ${descriptionExpanded ? 'expanded' : ''}`}>
                      ›
                    </span>
                    <span className="toggle-text">
                      {descriptionExpanded ? '折りたたむ' : 'もっと見る'}
                    </span>
                  </div>
                )}
              </div>
            </td>
          </tr>
        )}

        {/* ASIN + 市場名 */}
        <tr>
          <th>ASIN</th>
          <td>
            {productData.asin}
            <a
              href={`https://www.${getAmazonDomain(productData.marketplace_id)}/dp/${productData.asin}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-amazon-link"
            >
              Amazonで確認
            </a>
            {isFull && productData.sku && productData.marketplace_id && (
              <a
                href={`https://${getSellerCentralDomain(productData.marketplace_id)}/abis/listing/edit/images?sku=${encodeURIComponent(productData.sku)}&asin=${productData.asin}&marketplaceID=${productData.marketplace_id}#images`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-amazon-link"
              >
                Seller Centralで確認
              </a>
            )}
          </td>
          <th>市場名</th>
          <td>{getMarketplaceName(productData.marketplace_id)}</td>
        </tr>

        {/* SKU + GTIN */}
        <tr>
          <th>SKU</th>
          <td>{productData.sku || '-'}</td>
          <th>GTIN</th>
          <td>
            {productData.barcode_value
              ? `${productData.barcode_value} (${productData.barcode_type || 'Unknown'})`
              : '-'}
          </td>
        </tr>

        {/* モデル番号 + 色 */}
        <tr>
          <th>モデル番号</th>
          <td>{productData.model_number || '-'}</td>
          <th>色</th>
          <td>{productData.color || '-'}</td>
        </tr>

        {/* サイズ + 商品サイズ */}
        <tr>
          <th>サイズ</th>
          <td>{productData.size || '-'}</td>
          <th>商品サイズ</th>
          <td>{formatDimensions(productData.item_dimensions) || '-'}</td>
        </tr>

        {/* ブランド + メーカー */}
        <tr>
          <th>ブランド</th>
          <td>
            {brandDisplayName ? (
              <span style={{ display: 'inline-flex', alignItems: 'center', flexWrap: 'wrap' }}>
                {productData.brand_id ? (
                  <Link to={`/brands/${productData.brand_id}`} className="brand-link">
                    {brandDisplayName}
                  </Link>
                ) : (
                  productData.brand_name
                )}
                {productData.brand_id && !productData.brand_amazon_brand_entity_id && (
                  <BrandRegistryUnregisteredBadge />
                )}
              </span>
            ) : (
              '-'
            )}
            {/* 未紐づけなら既存ブランドとの照合を提案する (自動では紐づけない・再構成計画 §2.4) */}
            {isFull && productData.asin && <AmazonBrandLinkPrompt asin={productData.asin} brandId={productData.brand_id} brandName={productData.brand_name} />}
          </td>
          <th>メーカー</th>
          <td>{productData.manufacturer || '-'}</td>
        </tr>

        {/* タイプ + カテゴリ */}
        <tr>
          <th>商品タイプ</th>
          <td>{productData.product_type || '-'}</td>
          <th>カテゴリ</th>
          <td>
            {productData.category && productData.category.length > 0
              ? productData.category.join('、')
              : '-'}
          </td>
        </tr>

        {/* 価格 + 最終更新日 */}
        <tr>
          <th>価格</th>
          <td>{formatPrice(productData.price_amount, productData.price_currency) || '-'}</td>
          <th>最終更新日</th>
          <td>
            {productData.last_fetched_at
              ? new Date(productData.last_fetched_at).toLocaleString('ja-JP')
              : '-'}
          </td>
        </tr>

        {/* 情報取得ソース */}
        {isFull && (
          <tr>
            <th>情報ソース</th>
            <td colSpan={3}>
              {productData.data_source === 'sp_api' ? 'SP-API' : productData.data_source === 'scraping' ? 'スクレイピング' : '-'}
            </td>
          </tr>
        )}
      </tbody>
    </table>
  );
}

function BrandRegistryUnregisteredBadge() {
  return (
    <Tooltip content="Amazon Brand Registry 未登録">
      <span
        className="badge badge-muted"
        style={{ cursor: 'help', marginLeft: '8px' }}
      >
        他社
      </span>
    </Tooltip>
  );
}
